import { Component, Prop, Host, h, EventEmitter, Listen, Method, Event, Element } from '@stencil/core';
import { IconName } from '../icon/icons';

export type SelectItemValue = {
  value: string;
  label?: string;
  description: string;
};

@Component({
  tag: 'anchor-select-item',
  styleUrl: 'select-item.scss',
  shadow: true,
})
export class SelectItem {
  @Element() element: HTMLElement;

  /**
   *  Unique identificator for this DOM node
   */
  @Prop() itemId: string;

  /**
   *  Value to assigned to this particular SelectItem that will be emitted with the event when its selected
   */
  @Prop() value: string | SelectItemValue;

  /**
   *  Value to assigned to this particular SelectItem that will be emitted with the event when its selected
   */
  @Prop() description: string;

  /**
   *  Value indicating an icon to be shown inside of the SelectItem
   */
  @Prop() icon: IconName;

  /**
   *  Value indicating whether or not this particular SelectItem is already selected
   */
  @Prop({ reflect: true, mutable: true }) focused: boolean = false;

  /**
   *  Value indicating whether or not this particular SelectItem is already selected
   */
  @Prop({ reflect: true, mutable: true }) selected: boolean = false;

  /**
   *  Value indicating whether or not this particular SelectItem should be disabled from selecting
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   *  Value indicating whether or not this particular SelectItem should be readonly
   */
  @Prop({ reflect: true }) readonly: boolean = false;

  /**
   *  Event emitted when a SelectItem is pressed
   */
  @Event() pressed: EventEmitter;

  /**
   *  Event emitted when attempting to navigate to the next SelectItem
   */
  @Event() next: EventEmitter;

  /**
   *  Event emitted when attempting to navigate to the previous SelectItem
   */
  @Event() previous: EventEmitter;

  componentDidRender() {
    if (this.focused) {
      const button: HTMLElement = this.element.shadowRoot.querySelector('.anchor-c-select-item');
      button.focus();
    }
  }

  @Method()
  async emitSelectedEvent(e = undefined) {
    if (e && !this.readonly) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    if (!this.disabled && !this.readonly) {
      this.selected = !this.selected;
      this.pressed.emit({
        selected: this.selected,
        value: this.value,
        id: this.itemId,
      });
    }
  }

  @Listen('click')
  handleClick(e: PointerEvent) {
    this.emitSelectedEvent(e);
  }

  @Listen('keydown')
  handleKeyPress(e: KeyboardEvent) {
    switch (e.code) {
      case 'Enter':
      case 'Space':
        this.emitSelectedEvent(e);
        break;
      case 'ArrowDown':
        this.next.emit({ id: this.itemId });
        break;
      case 'ArrowUp':
        this.previous.emit({ id: this.itemId });
        break;
      default:
        break;
    }
  }

  render() {
    return (
      <Host>
        <button
          id={this.itemId}
          class={{
            'anchor-c-select-item': true,
            'readonly': this.readonly,
            'selected': this.selected,
          }}
          disabled={this.disabled}
        >
          <div class="top">
            <span class="value">{this.value ? this.value : <slot />}</span>
            {this.icon && <anchor-icon name={this.icon} size="small" class="icon" color={this.disabled ? 'var(--color-text-disabled)' : 'var(--color-icon-variant)'} stroke={2} />}
          </div>
          <span class="description">{this.description ? this.description : <slot name="description" />}</span>
        </button>
      </Host>
    );
  }
}
